import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { MaterialModule } from 'src/app/material.module';
import { LookupService } from 'src/app/services/lookup.service';
import { OPDService } from '../../opd/opd.service';
import { Router } from '@angular/router';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Iopd } from '../../opd/opddetails/iopddetails';
import { IpdService } from '../ipd.service';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { AllowOnlyDigitsDirective } from 'src/app/common/directive/allow-only-digits.directive';
import { ValidationService } from 'src/app/common/validation/validation.service';
import { Iipd } from '../Iipd.type';
import { ConvertTo24HourPipe } from 'src/app/pipe/convert-to24-hour.pipe';
import { formatDateOnlyForApi, parseApiDate } from 'src/app/common/function-helper';

@Component({
  selector: 'app-ipd-form',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
    AllowOnlyDigitsDirective
  ],
  providers: [
    ConvertTo24HourPipe,
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './ipd-form.component.html',
  styleUrl: './ipd-form.component.scss'
})
export class IpdFormComponent implements OnInit, OnDestroy {

  ipdform: FormGroup;
  wards: CommonDropDown[];
  roomTypes: CommonDropDown[];
  minDate: Date;
  maxDate: Date = new Date();

  wardddl: CommonDropDown[] = [];
  filteredward$: Observable<CommonDropDown[]>;
  wardCtrl = new FormControl<CommonDropDown | null>(null, Validators.required);

  mediclaimData$ = new BehaviorSubject<CommonDropDown[]>([]);
  filteredMediClaim$: Observable<CommonDropDown[]>;
  mediclaimCtrl = new FormControl<CommonDropDown | string | null>(null);

  roomNumbers: CommonDropDown[] = [];
  bedNumbers: CommonDropDown[] = [];
  filteredBeds$: Observable<CommonDropDown[]>;
  filteredRooms$: Observable<CommonDropDown[]>;

  anesthetistCtrl = new FormControl();
  showAddOptionforAnesthetist = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _isLoadingExistingData = false; // Flag to prevent valueChanges during initial load
  opddetails: Iopd;
  existingIpdData: Iipd | null = null;
  isEditMode: boolean = false;
  onPrintCallback?: () => void;
  readOnly: boolean = false;
  isLoading: boolean = true;
  pendingFollowUp: Pick<Iipd, 'followUpDate' | 'followUpTime'> | null = null;

  constructor(
    private _formbuilder: FormBuilder,
    private _lookupservice: LookupService,
    private _opdService: OPDService,
    private _ipdService: IpdService,
    @Inject(MAT_DIALOG_DATA) private _data: { OPDDetails: Iopd; existingIpdData?: Iipd; onPrint?: () => void; readOnly?: boolean },
    private dialogRef: MatDialogRef<IpdFormComponent>,
    private toastr: ToastrService,
    private router: Router,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private convertTo24HourPipe: ConvertTo24HourPipe
  ) {
    this.minDate = new Date(1800, 0, 1);
    this.opddetails = _data.OPDDetails;
    this.existingIpdData = _data.existingIpdData || null;
    this.isEditMode = !!this.existingIpdData;
    this.onPrintCallback = _data.onPrint;
    this.readOnly = _data.readOnly || false;
  }

  ngOnInit(): void {
    // Get current time in HH:mm format for default value
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const currentTime = `${hours}:${minutes}`;

    this.ipdform = this._formbuilder.group({
      ipdTime: [currentTime, [Validators.required]],
      ipdDate: [new Date(), [Validators.required]],
      wardId: ['', [Validators.required]],
      roomId: ['', [Validators.required]],
      bedId: ['', [Validators.required]],
      provisionalDiagnosis: ['', [Validators.required]],
      nameAndAddress: ['', [Validators.maxLength(500)]],
      contact: ['', [Validators.maxLength(10), ValidationService.phoneNumberValidator]],
      policyNumber: ['', [Validators.maxLength(50)]],
      mediclaimPlanId: [null]
    })

    this.initializeFilteredObservables();

    const loaders: Observable<any>[] = [
      this.loadMediClaims(),
      this.loadWards()
    ];

    if (!this.isEditMode) {
      loaders.push(this.loadOPDClinical(Number(this._data.OPDDetails.id)));
    }

    this.loadFollowUpFromIpd();

    combineLatest(loaders)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // isLoading is handled inside loadWards -> loadExistingIpdData for edit mode
        if (!this.isEditMode) {
          this.isLoading = false;
        }
      });

    this.mediclaimCtrl.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((mediclaim) => {
        if (!mediclaim || typeof mediclaim === 'string') {
          this.ipdform.patchValue({ mediclaimPlanId: null });
        }
      });


    // When ward changes, reload rooms filtered by ward
    this.wardCtrl.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((ward) => {
        // Skip if we're currently loading existing data (to prevent clearing rooms)
        if (this._isLoadingExistingData) {
          return;
        }

        // Clear room and bed selections when ward changes
        this.ipdform.get('roomId')?.setValue('');
        this.ipdform.get('bedId')?.setValue('');
        this.roomNumbers = [];
        this.bedNumbers = [];
        this.initializeFilteredObservables();

        if (ward?.id) {
          this.loadRooms(ward.id);
        }
      });

    // When room changes, reload beds for that room
    this.ipdform.get('roomId')?.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((room) => {
        // Clear bed selection when room changes
        this.ipdform.get('bedId')?.setValue('');
        if (room?.id) {
          this.loadBedNumbers(room.id);
        } else {
          this.bedNumbers = [];
        }
      });
  }

  get pendingFollowUpDate(): Date | null {
    return parseApiDate(this.pendingFollowUp?.followUpDate);
  }

  get pendingFollowUpTime(): string {
    return this.formatFollowUpTime(this.pendingFollowUp?.followUpTime);
  }

  private loadFollowUpFromIpd(): void {
    this.pendingFollowUp = this.existingIpdData?.followUpDate || this.existingIpdData?.followUpTime
      ? this.existingIpdData
      : null;
  }

  private formatFollowUpTime(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const timeMatch = value.match(/^(\d{1,2}):(\d{2})/);
    if (!timeMatch) {
      return value;
    }

    const hours = Number(timeMatch[1]);
    const minutes = timeMatch[2];
    const displayHour = hours % 12 || 12;
    const suffix = hours >= 12 ? 'PM' : 'AM';
    return `${displayHour}:${minutes} ${suffix}`;
  }

  loadWards(): Observable<any> {
    const checkAvailability = true; // Always check availability
    const includeIds = this.isEditMode && this.existingIpdData?.wardId
      ? [this.existingIpdData.wardId]
      : undefined;

    return this._lookupservice.getWards(undefined, checkAvailability, includeIds)
      .pipe(
        takeUntil(this._unsubscribeAll),
        tap((data) => {
          this.wardddl = data || [];
          if (this.wardddl.length === 0) {
            this.toastr.warning(this.translate.instant('VALIDATION.NO_WARD_AVAILABLE'));
          }
          this.filteredward$ = this.wardCtrl.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            switchMap(value => {
              const search = typeof value === 'string' ? value : value?.name || '';
              return this._lookupservice.getWards(search, checkAvailability, includeIds).pipe();
            })
          );

          // After wards are loaded, load existing IPD data if in edit mode
          if (this.isEditMode && this.existingIpdData) {
            this.loadExistingIpdData();
          }
        })
      );
  }


  private loadExistingIpdData(): void {
    if (!this.existingIpdData) {
      return;
    }

    this._isLoadingExistingData = true; // Set flag to prevent valueChanges from clearing rooms

    const ipdData = this.existingIpdData;

    const ward = this.wardddl.find(w => w.id === ipdData.wardId);

    if (!ward) {
      this._isLoadingExistingData = false;
      return;
    }

    this.wardCtrl.setValue(ward);
    this.ipdform.patchValue({ wardId: ward.id });

    // Handle Mediclaim
    if (ipdData.mediclaimPlanId) {
      this.mediclaimData$.pipe(take(1), takeUntil(this._unsubscribeAll))
        .subscribe((data: CommonDropDown[]) => {
          const mediclaim = data.find((m: CommonDropDown) => m.id === ipdData.mediclaimPlanId);
          if (mediclaim) {
            this.mediclaimCtrl.setValue(mediclaim);
            this.ipdform.patchValue({ mediclaimPlanId: mediclaim.id });
          }
        });
    }

    // Set other form values first
    let ipdTime = ipdData.ipdTime || '';
    if (ipdTime.includes(':')) {
      const timeParts = ipdTime.split(':');
      ipdTime = `${timeParts[0]}:${timeParts[1]}`;
    }

    this.ipdform.patchValue({
      ipdDate: parseApiDate(ipdData.ipdDate) ?? new Date(),
      ipdTime: ipdTime,
      provisionalDiagnosis: ipdData.provisionalDiagnosis || '',
      nameAndAddress: ipdData.nameAndAddress || '',
      contact: ipdData.contact || '',
      policyNumber: ipdData.policyNumber || ''
    });

    // Load rooms for this ward, then load beds, then set form values
    const checkAvailability = true; // Always check availability
    const includeRoomIds = ipdData.roomId ? [Number(ipdData.roomId)] : undefined;

    this._lookupservice.getRoomNumber(undefined, ward.id, checkAvailability, includeRoomIds)
      .pipe(
        takeUntil(this._unsubscribeAll),
        switchMap(rooms => {
          this.roomNumbers = rooms || [];

          if (ipdData.roomId) {
            const roomId = Number(ipdData.roomId);
            const room = this.roomNumbers.find(r => r.id === roomId);
            if (room) {
              // Set room value first
              this.ipdform.patchValue({ roomId: room });
              // Initialize filtered rooms AFTER setting the value
              this.initializeFilteredRooms();

              // Load beds for this room
              const includeBedIds = ipdData.bedId ? [Number(ipdData.bedId)] : undefined;
              return this._lookupservice.getBedNumbers(room.id, undefined, includeBedIds);
            } else {
              // Room ID exists but not found in list - might be occupied or API issue
              console.warn(`Room with ID ${roomId} not found in rooms list. Total rooms: ${this.roomNumbers.length}, IncludeIds: ${includeRoomIds?.join(',')}`);
            }
          }

          // If no room found, still initialize filtered rooms
          this.initializeFilteredRooms();
          return of([]);
        })
      )
      .subscribe({
        next: (beds) => {
          this.bedNumbers = beds || [];

          if (ipdData.bedId) {
            const bedId = Number(ipdData.bedId);
            const bed = this.bedNumbers.find(b => b.id === bedId);
            if (bed) {
              // Set bed value first
              this.ipdform.patchValue({ bedId: bed });
            } else {
              // Bed ID exists but not found in list - might be occupied or API issue
              console.warn(`Bed with ID ${bedId} not found in beds list. Total beds: ${this.bedNumbers.length}`);
            }
          }

          // Initialize filtered beds AFTER setting the value
          this.initializeFilteredBeds();
          this._isLoadingExistingData = false; // Reset flag after loading is complete
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading beds:', err);
          this.initializeFilteredBeds();
          this._isLoadingExistingData = false; // Reset flag on error too
          this.isLoading = false;
        }
      });
  }

  loadRooms(wardId?: number): void {
    const checkAvailability = true; // Always check availability
    // Only include current room if we're loading for the same ward (initial load)
    // If user changed ward, don't include old room from different ward
    const includeIds = this.isEditMode &&
      this.existingIpdData?.roomId &&
      wardId !== undefined &&
      Number(wardId) === Number(this.existingIpdData?.wardId)
      ? [this.existingIpdData.roomId]
      : undefined;

    this._lookupservice.getRoomNumber(undefined, wardId, checkAvailability, includeIds)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(res => {
        this.roomNumbers = res || [];
        if (wardId && this.roomNumbers.length === 0) {
          this.toastr.warning(this.translate.instant('VALIDATION.NO_ROOM_AVAILABLE'));
        }
        this.initializeFilteredRooms();
      });
  }

  private initializeFilteredObservables(): void {
    this.initializeFilteredRooms();
    this.initializeFilteredBeds();
    if (!this.filteredMediClaim$) {
      this.initializeFilteredMediClaims();
    }
  }

  private initializeFilteredMediClaims(): void {
    this.filteredMediClaim$ = combineLatest([
      this.mediclaimCtrl.valueChanges.pipe(
        startWith(''),
        debounceTime(200),
        distinctUntilChanged()
      ),
      this.mediclaimData$
    ]).pipe(
      map(([value, data]) => {
        if (!data?.length) {
          return [];
        }

        const searchText = this.getSearchText(value);

        return searchText
          ? data.filter(item =>
            item.name?.toLowerCase().includes(searchText)
          )
          : [...data];
      })
    );
  }

  private getSearchText(value: string | CommonDropDown | null): string {
    if (!value) {
      return '';
    }
    return (typeof value === 'string' ? value : value.name ?? '')
      .trim()
      .toLowerCase();
  }

  private initializeFilteredRooms(): void {
    const roomControl = this.ipdform.get('roomId')!;
    const currentValue = roomControl.value;

    this.filteredRooms$ = roomControl.valueChanges.pipe(
      startWith(currentValue),
      map(value => {
        if (!value || value === '') {
          return this.roomNumbers.slice();
        }
        const searchValue = typeof value === 'string' ? value : value?.name || '';
        if (!searchValue) {
          return this.roomNumbers.slice();
        }
        return this.filterRooms(searchValue);
      })
    );
  }

  private initializeFilteredBeds(): void {
    const bedControl = this.ipdform.get('bedId')!;
    const currentValue = bedControl.value;

    this.filteredBeds$ = bedControl.valueChanges.pipe(
      startWith(currentValue),
      map(value => {
        if (!value || value === '') {
          return this.bedNumbers.slice();
        }
        const searchValue = typeof value === 'string' ? value : value?.name || '';
        if (!searchValue) {
          return this.bedNumbers.slice();
        }
        return this.filterBeds(searchValue);
      })
    );
  }

  private filterRooms(value: string): CommonDropDown[] {
    const filterValue = value.toLowerCase();
    return this.roomNumbers.filter(r =>
      r.name.toLowerCase().includes(filterValue) ||
      r.childValue?.toLowerCase().includes(filterValue)
    );
  }

  loadBedNumbers(roomId?: number): void {
    // Only include current bed if we're loading for the same room (initial load)
    // If user changed room, don't include old bed from different room
    const includeIds = this.isEditMode &&
      this.existingIpdData?.bedId &&
      roomId !== undefined &&
      Number(roomId) === Number(this.existingIpdData?.roomId)
      ? [this.existingIpdData.bedId]
      : undefined;

    this._lookupservice.getBedNumbers(roomId, undefined, includeIds)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(res => {
        this.bedNumbers = res || [];
        if (roomId && this.bedNumbers.length === 0) {
          this.toastr.warning(this.translate.instant('VALIDATION.NO_BED_AVAILABLE'));
        }
        this.initializeFilteredBeds();
      });
  }
  private filterBeds(value: string): CommonDropDown[] {
    const filterValue = value.toLowerCase();
    return this.bedNumbers.filter(b =>
      b.name.toLowerCase().includes(filterValue)
    );
  }

  loadMediClaims(): Observable<any> {
    return this._lookupservice.getMediclaim().pipe(
      takeUntil(this._unsubscribeAll),
      tap((data) => {
        this.mediclaimData$.next(data);
      })
    );
  }

  displayWardName(ward?: CommonDropDown): string {
    return ward ? ward.name : '';
  }

  displayBedNumber(bed?: CommonDropDown): string {
    return bed ? bed.name : '';
  }

  displayRoomName(room?: CommonDropDown): string {
    return room ? `${room.childValue} (Room No: ${room.name})` : '';
  }

  // diagnosis load
  loadOPDClinical(opdId: number): Observable<any> {
    return this._opdService.getOPDClinical(opdId).pipe(
      tap((res: any) => {
        const items = res?.result || [];
        if (items.length === 0) return;
        const diagnosisList = items
          .map((x: any) => x.primaryDiagnosis?.trim() || '')
          .filter((x: string) => x !== '');

        const diagnosisText = diagnosisList.join(', ');

        this.ipdform.patchValue({
          provisionalDiagnosis: diagnosisText
        });
      })
    );
  }

  onSaveIPD() {
    if (this.ipdform.invalid) {
      this.ipdform.markAllAsTouched();
      return;
    }

    // Check ward availability
    if (!this.wardddl || this.wardddl.length === 0) {
      this.toastr.warning(this.translate.instant('VALIDATION.NO_WARD_AVAILABLE'));
      return;
    }

    const formData = this.ipdform.getRawValue();
    const selectedWardId = formData.wardId;
    const selectedRoom = formData.roomId;

    // Check room availability for selected ward
    if (selectedWardId && (!this.roomNumbers || this.roomNumbers.length === 0)) {
      return;
    }

    // Check bed availability for selected room (informational only, bed is optional)
    if (selectedRoom?.id && (!this.bedNumbers || this.bedNumbers.length === 0)) {
      return;
    }

    if (this.opddetails) {
      const payload: any = {
        ...formData,
        id: this.existingIpdData?.id,
        ipdNumber: this.existingIpdData?.ipdNumber || null,
        ipdDate: formatDateOnlyForApi(formData.ipdDate),
        ipdTime: this.convertTo24HourPipe.transform(formData.ipdTime),
        doctorId: this.opddetails.doctorId,
        opdId: this.opddetails.id,
        hospitalId: this.opddetails.hospitalId,
        roomId: formData.roomId?.id ?? null,
        bedId: formData.bedId?.id ?? null,
        policyNumber: formData.policyNumber,
        mediclaimPlanId: formData.mediclaimPlanId ?? null
      };

      if (this.isEditMode && this.existingIpdData?.id) {
        this._ipdService.updateIPD(payload).subscribe({
          next: (res) => {
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.IPD')));
            this.dialogRef.close({ updated: true, ipdData: res.result });
          },
          error: () => {
            this.toastr.error('Failed to update IPD');
          }
        });
      } else {
        // Create new IPD
        this._ipdService.createIPD(payload).subscribe({
          next: (res) => {
            this.router.navigate(['ipd/admit', res.result.id, 'patient', this.opddetails.patientId]);
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.IPD')));
            this.dialogRef.close();
          },
          error: () => {
            this.toastr.error('Failed to save IPD');
          }
        });
      }
    }
  }

  onPrint(): void {
    if (this.onPrintCallback) {
      this.onPrintCallback();
    }
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  displayward(ward: CommonDropDown): string {
    return ward && ward.name ? ward.name : '';
  }

  onWardSelected(ward: CommonDropDown) {
    this.ipdform.patchValue({
      wardId: ward.id
    });
    this.wardCtrl.setValue(ward);
  }

  displayMediclaim(mediclaim: CommonDropDown): string {
    return mediclaim?.name ?? '';
  }

  onMediClaimSelected(mediclaim: CommonDropDown) {
    this.ipdform.patchValue({
      mediclaimPlanId: mediclaim.id
    });
    this.mediclaimCtrl.setValue(mediclaim);
  }

}
